import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useDebounce } from "@unreserved/hooks/use-debounce";
import {
  type GeneralQueryFormData,
  generalQuerySchema,
} from "@unreserved/schemas/enquiries/general-query-schema";
import {
  GeneralEnquiryInputDtoQueryEnum,
  type PropertyListItemDto,
  type GeneralEnquiryInputDtoQueryEnum as QueryEnum,
} from "@unreserved/sdk";
import { Input } from "@unreserved/ui/components/input";
import { Label } from "@unreserved/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@unreserved/ui/components/select";
import { Textarea } from "@unreserved/ui/components/textarea";
import { Controller, useForm } from "react-hook-form";

import { useEnquiryModalDropdown } from "@/app/_components/my-enquiries/modal/enquiry-modal-dropdown-context";
import {
  useCreateGeneralEnquiry,
  usePropertiesForEnquiry,
} from "@/hooks/my-enquiry/use-my-enquiry";
import { useAuthStore } from "@/stores/auth-store";

import { PropertyAddressInput } from "../property-address-input";

export type GeneralEnquiryVariant = "contact" | "available-properties";

const CONTACT_QUERY_OPTIONS: { value: QueryEnum; label: string }[] = [
  { value: GeneralEnquiryInputDtoQueryEnum.Campaign, label: "Contact Unreserved" },
  { value: GeneralEnquiryInputDtoQueryEnum.AccountDelete, label: "Delete Account" },
];

const AVAILABLE_PROPERTIES_QUERY_OPTIONS: { value: QueryEnum; label: string }[] = [
  { value: GeneralEnquiryInputDtoQueryEnum.PropertyListing, label: "Property Listings" },
];

const QUERY_OPTIONS_BY_VARIANT: Record<
  GeneralEnquiryVariant,
  { value: QueryEnum; label: string }[]
> = {
  contact: CONTACT_QUERY_OPTIONS,
  "available-properties": AVAILABLE_PROPERTIES_QUERY_OPTIONS,
};

export const GeneralEnquiryForm = ({
  variant = "contact",
}: {
  variant?: GeneralEnquiryVariant;
}) => {
  const queryOptions = QUERY_OPTIONS_BY_VARIANT[variant];
  const user = useAuthStore((s) => s.user);
  const userEmail = user?.email != null ? String(user.email) : "";
  const userName = user?.fullName != null ? String(user.fullName) : "";
  const [selectedProperty, setSelectedProperty] = useState<PropertyListItemDto | null>(null);
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const [propertySearchText, setPropertySearchText] = useState("");
  const debouncedSearch = useDebounce(propertySearchText, 300);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<GeneralQueryFormData>({
    resolver: zodResolver(generalQuerySchema),
    mode: "onSubmit",
    defaultValues: {
      name: "",
      email: "",
      query: "",
      propertyAddress: "",
      queryDetails: "",
    },
  });

  const selectedQuery = watch("query");

  const {
    data: propertiesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = usePropertiesForEnquiry({ propertyTitle: debouncedSearch || undefined });

  const properties = useMemo(
    () => propertiesData?.pages?.flatMap((page) => page.data?.properties ?? []) ?? [],
    [propertiesData],
  );

  const propertySuggestions = useMemo(
    () =>
      properties.map((p) => ({
        id: p.id,
        displayLabel: p.title != null && typeof p.title === "string" ? p.title : p.id,
        data: p as PropertyListItemDto,
      })),
    [properties],
  );

  const createGeneralEnquiry = useCreateGeneralEnquiry();
  const { setIsSubmitting, setSuccessConfig, preselectedListingId } = useEnquiryModalDropdown();

  useEffect(() => {
    setIsSubmitting(createGeneralEnquiry.isPending);
  }, [createGeneralEnquiry.isPending, setIsSubmitting]);

  useEffect(() => {
    if (userName) setValue("name", userName);
  }, [userName, setValue]);
  useEffect(() => {
    if (userEmail) setValue("email", userEmail);
  }, [userEmail, setValue]);

  useEffect(() => {
    if (queryOptions.length === 1 && !selectedQuery) {
      setValue("query", queryOptions[0].value);
    }
  }, [queryOptions, selectedQuery, setValue]);

  useEffect(() => {
    if (preselectedListingId && properties.length > 0 && !selectedProperty) {
      if (!selectedQuery && variant === "available-properties") {
        setValue("query", GeneralEnquiryInputDtoQueryEnum.PropertyListing);
      }
      const match = properties.find(
        (p) => p.id === preselectedListingId || String(p.listingId) === preselectedListingId,
      );
      if (match) {
        setSelectedProperty(match);
        setValue("propertyAddress", typeof match.title === "string" ? match.title : match.id);
      }
    }
  }, [preselectedListingId, properties, selectedProperty, selectedQuery, setValue, variant]);

  const onSubmit = (data: GeneralQueryFormData) => {
    const isPropertyListingQuery = data.query === GeneralEnquiryInputDtoQueryEnum.PropertyListing;
    if (isPropertyListingQuery && !selectedProperty?.listingId) return;
    const listingId = selectedProperty?.listingId
      ? typeof selectedProperty.listingId === "string"
        ? selectedProperty.listingId
        : String(selectedProperty.listingId)
      : undefined;
    createGeneralEnquiry.mutate(
      {
        name: data.name,
        email: data.email,
        ...(listingId ? { listingId } : {}),
        query: data.query as GeneralEnquiryInputDtoQueryEnum,
        details: data.queryDetails,
      },
      {
        onSuccess: () => {
          setSuccessConfig({
            title: "Your query has been added successfully !!",
            subheading: "Team will review and get back , you'll be notified once responded",
            primaryButtonLabel: "Back to Home",
            primaryButtonRedirect: "dashboard",
            extraButton: true,
            extraButtonLabel: "Back",
          });
        },
      },
    );
  };

  return (
    <form id="general-enquiry-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-5">
        {/* Email */}
        <div className="space-y-1.5">
          <Label className="text-typography-750 text-sm font-medium">
            Name
            {/* <span className="text-error-500">*</span> */}
          </Label>
          <Input
            type="text"
            placeholder="Enter here"
            className="border-border-400 text-typography-900 placeholder:text-typography-400 focus-visible:border-primary-500 focus-visible:ring-primary-200 h-9 w-full rounded border bg-white! px-3 text-sm focus-visible:ring-4 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-70"
            {...register("name")}
            aria-invalid={errors.name ? "true" : "false"}
            disabled={!!userName}
          />
          {errors.name && <p className="text-error-600 text-sm">{errors.name.message}</p>}
        </div>

        {/* Email */}
        <div className="space-y-1.5">
          <Label className="text-typography-750 text-sm font-medium">
            Email ID
            {/* <span className="text-error-500">*</span> */}
          </Label>
          <Input
            type="email"
            placeholder="Enter here"
            className="border-border-400 text-typography-900 placeholder:text-typography-400 focus-visible:border-primary-500 focus-visible:ring-primary-200 h-9 w-full rounded border bg-white! px-3 text-sm focus-visible:ring-4 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-70"
            {...register("email")}
            aria-invalid={errors.email ? "true" : "false"}
            disabled={!!userEmail}
          />
          {errors.email && <p className="text-error-600 text-sm">{errors.email.message}</p>}
        </div>

        {/* Query */}
        <div className="space-y-1.5">
          <Label className="text-typography-750 text-sm font-medium">
            Select Query <span className="text-error-500">*</span>
          </Label>
          <Controller
            control={control}
            name="query"
            render={({ field }) => (
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <SelectTrigger className="border-border-400 text-typography-900 placeholder:text-typography-400 focus-visible:border-primary-500 focus-visible:ring-primary-200 h-9 w-full rounded border bg-white! px-3 text-sm focus-visible:ring-4 focus-visible:outline-none">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent side="bottom" className="cursor-pointer bg-white! text-black">
                  {queryOptions.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      className="bg-white! text-black focus:bg-white! focus:text-black">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.query && <p className="text-error-600 text-sm">{errors.query.message}</p>}
        </div>

        {/* Property Address */}
        {selectedQuery === GeneralEnquiryInputDtoQueryEnum.PropertyListing && (
          <div className={`space-y-1.5 ${isPropertyDropdownOpen ? "mb-40" : ""}`}>
            <Label className="text-typography-750 text-sm font-medium">
              Select Property <span className="text-error-500">*</span>
            </Label>
            <div className="relative">
              <Controller
                control={control}
                name="propertyAddress"
                render={({ field }) => {
                  const selectedDisplayLabel =
                    selectedProperty != null
                      ? typeof selectedProperty.title === "string"
                        ? selectedProperty.title
                        : selectedProperty.id
                      : "";
                  return (
                    <PropertyAddressInput
                      value={field.value ?? ""}
                      onChange={(value) => {
                        field.onChange(value);
                        setPropertySearchText(value);
                        if (value === "" || value !== selectedDisplayLabel) {
                          setSelectedProperty(null);
                        }
                      }}
                      placeholder="Select property"
                      externalSuggestions={propertySuggestions}
                      onSelectSuggestion={(item) => {
                        if (item.data) {
                          setSelectedProperty(item.data);
                          field.onChange(item.displayLabel);
                          setPropertySearchText("");
                        }
                      }}
                      onDropdownOpenChange={(open) => {
                        setIsPropertyDropdownOpen(open);
                      }}
                      onEndReached={() => {
                        if (hasNextPage && !isFetchingNextPage) {
                          void fetchNextPage();
                        }
                      }}
                      loadingMore={isFetchingNextPage}
                      hasMore={hasNextPage}
                      loading={isLoading}
                    />
                  );
                }}
              />
            </div>
            {errors.propertyAddress && (
              <p className="text-error-600 text-sm">{errors.propertyAddress.message}</p>
            )}
          </div>
        )}

        {/* Query Details */}
        <div className="space-y-1.5">
          <Label className="text-typography-750 text-sm font-medium">
            Write Your Query Details <span className="text-error-500">*</span>
          </Label>
          <Textarea
            placeholder="Enter here"
            className="border-border-400 text-typography-900 placeholder:text-typography-400 focus-visible:border-primary-500 focus-visible:ring-primary-200 h-11 w-full rounded border bg-white! px-3 text-sm focus-visible:ring-4 focus-visible:outline-none"
            {...register("queryDetails")}
            aria-invalid={errors.queryDetails ? "true" : "false"}
          />
          {errors.queryDetails && (
            <p className="text-error-600 text-sm">{errors.queryDetails.message}</p>
          )}
        </div>
      </div>
    </form>
  );
};

export const ContactUnreservedEnquiryForm = () => <GeneralEnquiryForm variant="contact" />;

export const AvailablePropertiesEnquiryForm = () => (
  <GeneralEnquiryForm variant="available-properties" />
);
